export type DocType = "latex" | "markdown" | "docx";

export interface DemoTemplate {
  id: string;
  label: string;
  description: string;
  doc_type: DocType;
  content: string;
}

const LATEX_TEMPLATES: Record<string, string> = {
  "blank-latex":
    "\\documentclass{article}\n\\usepackage[utf8]{inputenc}\n\n" +
    "\\title{Untitled}\n\\author{}\n\\date{}\n\n" +
    "\\begin{document}\n\\maketitle\n\n\n\n\\end{document}\n",

  paper: String.raw`\documentclass[12pt]{article}
\usepackage[utf8]{inputenc}
\usepackage{amsmath,amssymb,amsthm}
\usepackage{geometry}
\usepackage{hyperref}
\usepackage{graphicx}
\usepackage{natbib}

\geometry{margin=1in}

\title{Your Title Here}
\author{Your Name}
\date{\today}

\begin{document}
\maketitle

\begin{abstract}
Your abstract here.
\end{abstract}

\section{Introduction}

\section{Related Work}

\section{Methods}

\section{Results}

\section{Discussion}

\section{Conclusion}

\bibliographystyle{plain}
\bibliography{references}

\end{document}
`,

  pset: String.raw`\documentclass[12pt]{article}
\usepackage[utf8]{inputenc}
\usepackage{amsmath,amssymb,amsthm}
\usepackage{geometry}
\usepackage{enumerate}
\usepackage{fancyhdr}

\geometry{margin=1in}
\pagestyle{fancy}

\lhead{Your Name}
\chead{Problem Set Title}
\rhead{\today}

\newcommand{\problem}[1]{\section*{Problem #1}}
\newcommand{\solution}{\subsection*{Solution}}

\begin{document}

\begin{center}
\Large\textbf{Problem Set Title} \\
\normalsize Your Name \\
\today
\end{center}

\problem{1}
Problem statement here.

\solution
Your solution here.

\problem{2}
Problem statement here.

\solution
Your solution here.

\end{document}
`,

  "pset-answers": String.raw`\documentclass[11pt]{article}
\usepackage{graphicx}
\usepackage{fullpage}
\usepackage{amsfonts}
\usepackage{amssymb}
\usepackage{amsmath}
\usepackage{xcolor}
\usepackage{algorithm}
\usepackage{algorithmic}
\usepackage{enumitem}
\usepackage{hyperref}

\newenvironment{answer}
  {\color{blue}}
{}

\newcommand{\F}{\mathbb{F}}
\newcommand{\Z}{\mathbb{Z}}
\newcommand{\R}{\mathbb{R}}
\newcommand{\N}{\mathbb{N}}
\newcommand{\np}{\mathop{\rm NP}}
\newcommand{\vol}{\mathop{\rm Vol}}

\setlength{\parskip}{\medskipamount}
\setlength{\parindent}{0in}

\begin{document}

\section*{Course --- Problem Set Title}

\textbf{Your Name}

\textbf{Collaborators: }

\section*{Problems}

\begin{enumerate}

\item
\textbf{(10 points)} Problem statement here.

\begin{answer}
Your solution here.
\end{answer}

\item
\textbf{(10 points)} Problem statement here.

\begin{answer}
Your solution here.
\end{answer}

\end{enumerate}

\end{document}
`,

  "pset-formal": String.raw`\documentclass[12pt]{article}

\usepackage[margin=1in]{geometry}
\usepackage{amsmath, amssymb, amsthm, mathtools}
\usepackage{enumitem, booktabs, bm}
\usepackage[dvipsnames]{xcolor}
\usepackage{hyperref}
\usepackage{fancyhdr}

\setlength{\parindent}{0pt}
\setlength{\parskip}{0.5em}

\hypersetup{
  colorlinks=true,
  linkcolor=NavyBlue,
  urlcolor=NavyBlue,
  citecolor=NavyBlue
}

\pagestyle{fancy}
\fancyhf{}
\lhead{Course Name}
\chead{Your Name}
\rhead{\today}
\cfoot{\thepage}

\newcommand{\E}{\mathbb{E}}
\newcommand{\Var}{\operatorname{Var}}
\newcommand{\Cov}{\operatorname{Cov}}
\newcommand{\Prob}{\mathbb{P}}
\newcommand{\R}{\mathbb{R}}
\newcommand{\N}{\mathbb{N}}
\newcommand{\Z}{\mathbb{Z}}

\newcommand{\psetheader}[3]{%
  {\noindent\large\textbf{#1} \hfill #2 \\[0.25em]
  \textit{#3}} \\[1em] \hrule \vspace{1.5em}
}

\setlist[enumerate,1]{label=\arabic*., wide=0pt, leftmargin=*}
\setlist[enumerate,2]{label=(\alph*), wide=0pt, leftmargin=*}

\begin{document}

\psetheader{Problem Set Title}{Your Name}{\today}

\section*{Problem 1}

\begin{enumerate}
    \item
    \begin{enumerate}
        \item Part (a) solution here.
        \item Part (b) solution here.
    \end{enumerate}
\end{enumerate}

\section*{Problem 2}

\begin{enumerate}
    \item Solution here.
\end{enumerate}

\end{document}
`,

  report: String.raw`\documentclass[12pt]{article}
\usepackage[utf8]{inputenc}
\usepackage{amsmath,amssymb}
\usepackage{geometry}
\usepackage{hyperref}
\usepackage{graphicx}
\usepackage{booktabs}
\usepackage{caption}

\geometry{margin=1in}

\title{Report Title}
\author{Your Name}
\date{\today}

\begin{document}
\maketitle
\tableofcontents
\newpage

\section{Introduction}

\section{Background}

\section{Methodology}

\section{Results}

\section{Conclusion}

\end{document}
`,

  letter: String.raw`\documentclass[12pt]{letter}
\usepackage[utf8]{inputenc}
\usepackage{geometry}

\geometry{margin=1in}

\signature{Your Name}
\address{Your Address}

\begin{document}
\begin{letter}{Recipient Name \\ Recipient Address}

\opening{Dear Sir/Madam,}

Your letter content here.

\closing{Sincerely,}

\end{letter}
\end{document}
`,

  beamer: String.raw`\documentclass{beamer}
\usepackage[utf8]{inputenc}
\usepackage{amsmath,amssymb}
\usepackage{graphicx}

\usetheme{Madrid}
\usecolortheme{default}

\title{Presentation Title}
\author{Your Name}
\institute{Your Institution}
\date{\today}

\begin{document}

\frame{\titlepage}

\begin{frame}{Outline}
\tableofcontents
\end{frame}

\section{Introduction}
\begin{frame}{Introduction}
Content here.
\end{frame}

\section{Main Content}
\begin{frame}{Main Content}
Content here.
\end{frame}

\section{Conclusion}
\begin{frame}{Conclusion}
Content here.
\end{frame}

\end{document}
`,
};

const MARKDOWN_TEMPLATES: Record<string, string> = {
  "blank-md": "# Untitled\n\n",

  "markdown-example": `# Markdown Example

## Introduction

This is a **rich Markdown document** demonstrating the editor's capabilities. You can write *italics*, **bold**, and \`inline code\` seamlessly.

## Math Support

Lever supports LaTeX math via KaTeX. Inline math like $E = mc^2$ works naturally.

Display math is also supported:

$$
\\int_{-\\infty}^{\\infty} e^{-x^2} \\, dx = \\sqrt{\\pi}
$$

The quadratic formula:

$$
x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}
$$

## Code Blocks

\`\`\`python
def fibonacci(n: int) -> list[int]:
    """Generate the first n Fibonacci numbers."""
    fibs = [0, 1]
    for _ in range(2, n):
        fibs.append(fibs[-1] + fibs[-2])
    return fibs[:n]

print(fibonacci(10))
\`\`\`

## Tables

| Feature      | LaTeX | Markdown | DOCX |
|-------------|-------|----------|------|
| Math        | Yes   | Yes      | No   |
| Code blocks | Yes   | Yes      | No   |
| WYSIWYG     | No    | No       | Yes  |
| PDF output  | Yes   | No       | Yes  |

## Lists

### Unordered

- First item
- Second item
  - Nested item
  - Another nested item
- Third item

### Ordered

1. Step one
2. Step two
3. Step three

## Blockquotes

> "Give me a lever long enough and a fulcrum on which to place it, and I shall move the world."
>
> --- Archimedes

## Links and Images

Visit [GitHub](https://github.com) for more information.

---

*This document is rendered entirely client-side with no backend required.*
`,
};

const DOCX_TEMPLATES: Record<string, string> = {
  "blank-docx": "<h1>Untitled Document</h1><p></p>",

  essay: `<h1 style="text-align: center">Essay Title</h1>
<p style="text-align: center">Your Name<br>Course Name<br>Instructor Name<br>Date</p>
<p></p>
<h2>Introduction</h2>
<p>Begin your introduction here. Present your thesis statement and provide context for your argument. A strong introduction captures the reader's attention and outlines the main points you will discuss.</p>
<p></p>
<h2>Body Paragraph 1</h2>
<p>Present your first main argument or point. Include a topic sentence, supporting evidence, analysis, and a transition to the next paragraph. Use citations where appropriate.</p>
<p></p>
<h2>Body Paragraph 2</h2>
<p>Develop your second main argument. Build on the previous section and introduce new evidence. Make sure each paragraph connects logically to your thesis.</p>
<p></p>
<h2>Body Paragraph 3</h2>
<p>Present additional arguments or counterarguments. Address potential objections and strengthen your position with further analysis.</p>
<p></p>
<h2>Conclusion</h2>
<p>Summarize your main arguments and restate your thesis in light of the evidence presented. Discuss broader implications and end with a thought-provoking closing statement.</p>
<p></p>
<h2>Works Cited</h2>
<p>Last, First. <em>Title of Work</em>. Publisher, Year.</p>`,

  "research-paper": `<h1 style="text-align: center">Research Paper Title</h1>
<p style="text-align: center"><strong>Author Name</strong><br>Department, Institution<br>email@example.com</p>
<p></p>
<h2>Abstract</h2>
<p>Provide a concise summary of your research (150-300 words). State the problem, your approach, key results, and conclusions.</p>
<p><strong>Keywords:</strong> keyword1, keyword2, keyword3</p>
<p></p>
<h2>1. Introduction</h2>
<p>Introduce the research problem and its significance. Review relevant background and state your research questions or hypotheses. Outline the structure of the paper.</p>
<p></p>
<h2>2. Literature Review</h2>
<p>Discuss prior work relevant to your research. Identify gaps in the existing literature that your work addresses. Organize thematically or chronologically.</p>
<p></p>
<h2>3. Methodology</h2>
<h3>3.1 Research Design</h3>
<p>Describe your overall research approach (qualitative, quantitative, mixed methods).</p>
<h3>3.2 Data Collection</h3>
<p>Detail how data was gathered, including instruments, participants, and procedures.</p>
<h3>3.3 Data Analysis</h3>
<p>Explain analytical methods and tools used to process the data.</p>
<p></p>
<h2>4. Results</h2>
<p>Present your findings objectively. Use tables and figures where appropriate to illustrate key data points.</p>
<table>
<tr><th>Variable</th><th>Group A</th><th>Group B</th><th>p-value</th></tr>
<tr><td>Measure 1</td><td>\u2014</td><td>\u2014</td><td>\u2014</td></tr>
<tr><td>Measure 2</td><td>\u2014</td><td>\u2014</td><td>\u2014</td></tr>
</table>
<p><em>Table 1: Summary of key results.</em></p>
<p></p>
<h2>5. Discussion</h2>
<p>Interpret your results in the context of existing literature. Discuss implications, limitations, and suggestions for future research.</p>
<p></p>
<h2>6. Conclusion</h2>
<p>Summarize the key contributions of your research and their broader impact.</p>
<p></p>
<h2>References</h2>
<p>[1] Author, A. (Year). Title of article. <em>Journal Name</em>, Volume(Issue), Pages.</p>
<p>[2] Author, B. (Year). <em>Title of Book</em>. Publisher.</p>`,

  resume: `<h1>Your Name</h1>
<p>email@example.com &bull; (555) 123-4567 &bull; City, State &bull; <a href="https://linkedin.com/in/yourprofile">LinkedIn</a> &bull; <a href="https://github.com/yourprofile">GitHub</a></p>
<hr>
<h2>Education</h2>
<p><strong>University Name</strong> \u2014 <em>Degree, Major</em><br>Expected Graduation: Month Year &bull; GPA: X.XX/4.00</p>
<ul>
<li>Relevant coursework: Course 1, Course 2, Course 3</li>
<li>Honors: Dean's List, Scholarship Name</li>
</ul>
<p></p>
<h2>Experience</h2>
<p><strong>Company Name</strong> \u2014 <em>Job Title</em><br>Month Year \u2013 Month Year &bull; City, State</p>
<ul>
<li>Accomplished [X] as measured by [Y] by doing [Z]</li>
<li>Led a team of [N] to deliver [project/result] on time and under budget</li>
<li>Improved [process/metric] by [X]% through [specific action]</li>
</ul>
<p><strong>Company Name</strong> \u2014 <em>Job Title</em><br>Month Year \u2013 Month Year &bull; City, State</p>
<ul>
<li>Developed [feature/product] using [technology], resulting in [impact]</li>
<li>Collaborated with [team/department] to [achieve outcome]</li>
</ul>
<p></p>
<h2>Projects</h2>
<p><strong>Project Name</strong> \u2014 <em>Technologies Used</em></p>
<ul>
<li>Brief description of the project, its purpose, and your role</li>
<li>Key technical achievements or metrics</li>
</ul>
<p></p>
<h2>Skills</h2>
<p><strong>Languages:</strong> Python, JavaScript, TypeScript, Java, SQL</p>
<p><strong>Frameworks:</strong> React, Node.js, FastAPI, Django</p>
<p><strong>Tools:</strong> Git, Docker, AWS, PostgreSQL, Linux</p>
<p></p>
<h2>Activities &amp; Leadership</h2>
<ul>
<li><strong>Organization Name</strong> \u2014 Role (Year \u2013 Year): Brief description</li>
<li><strong>Volunteer Work</strong> \u2014 Description of contribution</li>
</ul>`,

  "docx-report": `<h1 style="text-align: center">Report Title</h1>
<p style="text-align: center">Prepared by: Your Name<br>Organization / Course<br>Date</p>
<p></p>
<h2>Table of Contents</h2>
<ol>
<li>Executive Summary</li>
<li>Introduction</li>
<li>Background</li>
<li>Methodology</li>
<li>Findings</li>
<li>Analysis</li>
<li>Recommendations</li>
<li>Conclusion</li>
<li>Appendices</li>
</ol>
<p></p>
<h2>1. Executive Summary</h2>
<p>Provide a brief overview of the report's purpose, key findings, and recommendations. This section should stand alone as a summary for readers who may not read the full report.</p>
<p></p>
<h2>2. Introduction</h2>
<p>State the purpose and scope of the report. Define the problem or question being addressed and outline your approach.</p>
<p></p>
<h2>3. Background</h2>
<p>Provide context and background information necessary for understanding the report. Include relevant history, prior work, or regulatory framework.</p>
<p></p>
<h2>4. Methodology</h2>
<p>Describe the methods used to gather and analyze information. Include data sources, tools, and any limitations of your approach.</p>
<p></p>
<h2>5. Findings</h2>
<p>Present the key facts and data discovered during your research or investigation. Use tables, charts, and bullet points for clarity.</p>
<table>
<tr><th>Metric</th><th>Current</th><th>Target</th><th>Status</th></tr>
<tr><td>Metric 1</td><td>\u2014</td><td>\u2014</td><td>On Track</td></tr>
<tr><td>Metric 2</td><td>\u2014</td><td>\u2014</td><td>At Risk</td></tr>
</table>
<p></p>
<h2>6. Analysis</h2>
<p>Interpret the findings. Discuss patterns, causes, and implications. Compare against benchmarks or expectations.</p>
<p></p>
<h2>7. Recommendations</h2>
<ol>
<li><strong>Recommendation 1:</strong> Description and rationale</li>
<li><strong>Recommendation 2:</strong> Description and rationale</li>
<li><strong>Recommendation 3:</strong> Description and rationale</li>
</ol>
<p></p>
<h2>8. Conclusion</h2>
<p>Summarize the report's main points and reiterate the most important recommendations.</p>
<p></p>
<h2>9. Appendices</h2>
<p>Include supplementary materials, raw data, or detailed calculations referenced in the report.</p>`,

  "cover-letter": `<p>Your Name<br>Your Address<br>City, State ZIP<br>email@example.com<br>(555) 123-4567</p>
<p>Date</p>
<p>Hiring Manager's Name<br>Company Name<br>Company Address<br>City, State ZIP</p>
<p></p>
<p>Dear Hiring Manager,</p>
<p></p>
<p>I am writing to express my interest in the <strong>[Position Title]</strong> role at <strong>[Company Name]</strong>. With my background in [relevant field/experience], I am confident in my ability to contribute meaningfully to your team.</p>
<p></p>
<p>In my current role at [Current Company], I have [describe a key accomplishment or responsibility that is relevant to the position]. This experience has equipped me with [specific skills] that directly align with the requirements of this position.</p>
<p></p>
<p>I am particularly drawn to [Company Name] because of [specific reason \u2014 company mission, recent project, culture, etc.]. I believe my [specific skills or qualities] would allow me to [how you would contribute to the company's goals].</p>
<p></p>
<p>I would welcome the opportunity to discuss how my experience and skills can benefit your team. Thank you for your time and consideration. I look forward to hearing from you.</p>
<p></p>
<p>Sincerely,<br>Your Name</p>`,

  "meeting-notes": `<h1>Meeting Notes</h1>
<p><strong>Date:</strong> [Date]<br><strong>Time:</strong> [Start Time] \u2013 [End Time]<br><strong>Location:</strong> [Room / Video Link]<br><strong>Facilitator:</strong> [Name]<br><strong>Note Taker:</strong> [Name]</p>
<p></p>
<h2>Attendees</h2>
<ul>
<li>[Name] \u2014 [Role]</li>
<li>[Name] \u2014 [Role]</li>
<li>[Name] \u2014 [Role]</li>
</ul>
<p></p>
<h2>Agenda</h2>
<ol>
<li>[Topic 1]</li>
<li>[Topic 2]</li>
<li>[Topic 3]</li>
</ol>
<p></p>
<h2>Discussion Notes</h2>
<h3>Topic 1</h3>
<ul>
<li>Key point discussed</li>
<li>Decision made or deferred</li>
</ul>
<h3>Topic 2</h3>
<ul>
<li>Key point discussed</li>
<li>Questions raised</li>
</ul>
<h3>Topic 3</h3>
<ul>
<li>Key point discussed</li>
<li>Follow-up needed</li>
</ul>
<p></p>
<h2>Action Items</h2>
<table>
<tr><th>Action</th><th>Owner</th><th>Due Date</th><th>Status</th></tr>
<tr><td>[Task description]</td><td>[Name]</td><td>[Date]</td><td>Pending</td></tr>
<tr><td>[Task description]</td><td>[Name]</td><td>[Date]</td><td>Pending</td></tr>
</table>
<p></p>
<h2>Next Meeting</h2>
<p><strong>Date:</strong> [Date]<br><strong>Time:</strong> [Time]<br><strong>Agenda Preview:</strong> [Topics to cover]</p>`,
};

export const DEMO_TEMPLATES: DemoTemplate[] = [
  {
    id: "paper",
    label: "Paper",
    description: "Academic paper with abstract, sections, and bibliography",
    doc_type: "latex",
    content: LATEX_TEMPLATES.paper,
  },
  {
    id: "pset",
    label: "Problem Set",
    description: "Simple problem set with numbered problems",
    doc_type: "latex",
    content: LATEX_TEMPLATES.pset,
  },
  {
    id: "pset-answers",
    label: "Problem Set (Answers)",
    description: "Problem set with colored answer blocks and math shortcuts",
    doc_type: "latex",
    content: LATEX_TEMPLATES["pset-answers"],
  },
  {
    id: "pset-formal",
    label: "Problem Set (Formal)",
    description: "Formal problem set with header/footer and stats shortcuts",
    doc_type: "latex",
    content: LATEX_TEMPLATES["pset-formal"],
  },
  {
    id: "report",
    label: "Report",
    description: "Academic or lab report with table of contents",
    doc_type: "latex",
    content: LATEX_TEMPLATES.report,
  },
  {
    id: "letter",
    label: "Letter",
    description: "Formal letter template",
    doc_type: "latex",
    content: LATEX_TEMPLATES.letter,
  },
  {
    id: "beamer",
    label: "Presentation",
    description: "Beamer slide deck",
    doc_type: "latex",
    content: LATEX_TEMPLATES.beamer,
  },
  {
    id: "markdown-example",
    label: "Markdown Example",
    description: "Rich example with math, code, tables, and lists",
    doc_type: "markdown",
    content: MARKDOWN_TEMPLATES["markdown-example"],
  },
  {
    id: "essay",
    label: "Essay",
    description: "Structured essay with introduction, body, and conclusion",
    doc_type: "docx",
    content: DOCX_TEMPLATES.essay,
  },
  {
    id: "research-paper",
    label: "Research Paper",
    description: "Research paper with abstract, methodology, and references",
    doc_type: "docx",
    content: DOCX_TEMPLATES["research-paper"],
  },
  {
    id: "resume",
    label: "Resume",
    description: "Professional resume with education, experience, and skills",
    doc_type: "docx",
    content: DOCX_TEMPLATES.resume,
  },
  {
    id: "docx-report",
    label: "Report (Document)",
    description: "Formal report with executive summary and recommendations",
    doc_type: "docx",
    content: DOCX_TEMPLATES["docx-report"],
  },
  {
    id: "cover-letter",
    label: "Cover Letter",
    description: "Professional cover letter template",
    doc_type: "docx",
    content: DOCX_TEMPLATES["cover-letter"],
  },
  {
    id: "meeting-notes",
    label: "Meeting Notes",
    description: "Meeting notes with agenda, discussion, and action items",
    doc_type: "docx",
    content: DOCX_TEMPLATES["meeting-notes"],
  },
];

export function getDemoTemplate(id: string): DemoTemplate | undefined {
  return DEMO_TEMPLATES.find((t) => t.id === id);
}

export function getDemoTemplatesByType(docType: DocType): DemoTemplate[] {
  return DEMO_TEMPLATES.filter((t) => t.doc_type === docType);
}
